import { Button, Tooltip, Typography } from '@mui/material';
import { AccountingPeriodStatus } from 'further-types/lp/accounting-period';
import ProcessingPage from 'components/new/ProcessingPage';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Row } from 'components/Layout';
import { useLayoutContext } from 'contexts/LayoutContext';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { LP_PERIOD_END_REVIEW } from 'adminConstants/routes';
import Page from 'components/new/Page';
import PeriodEndReviewChecklist from './PeriodEndReviewChecklist';
import { requiredItems } from './checklistConfig';
import { usePeriodEndSidebar } from './usePeriodEndSidebar';
import { match } from 'ts-pattern';
import PostUnrealisedFXGains from 'routes/LpPages/AccountingCalendar/PostUnrealisedFXGains';
import BulkRevaluations from 'routes/LpPages/Company/Revaluations/BulkRevaluations';
import ApprovePrepayments from 'routes/LpPages/AccountingCalendar/ApprovePrepayments';
import ReviewAccruals from 'routes/LpPages/AccountingCalendar/ReviewAccruals';
import ApproveTransactionRates from 'routes/LpPages/AccountingCalendar/ApproveTransactionRates';
import BankBalances from 'routes/LpPages/PeriodEndReview/BankBalances';

type RouteParams = {
  periodId: string;
};

const PeriodEndReviewDetail: React.FC = () => {
  const { activeSidebar, openSidebar, closeSidebar } = usePeriodEndSidebar();
  const { periodId } = useParams<RouteParams>();
  const { vehicle, setBreadcrumbs, breadcrumbs } = useLayoutContext();
  const handleMutation = useHandleTrpcMutation<string>();
  const handleMutationGeneric = useHandleTrpcMutation();
  const { success } = useNotification();

  const [period] = api.accountingPeriod.getPeriodById.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
    id: periodId,
  });

  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [refreshModalOpen, setRefreshModalOpen] = useState(period.needsRefresh);
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);

  const approvePeriodMutation = api.accountingPeriod.approvePeriod.useMutation(
    handleMutation({ refetchType: 'active' }),
  );

  const resetPeriodMutation =
    api.accountingPeriod.resetPeriodEndReview.useMutation(
      handleMutationGeneric({
        successMessage: 'Period end review has been reset.',
        refetchType: 'active',
        onSuccess: () => setRefreshModalOpen(false),
      }),
    );

  const [canEditPeriod] = useHasPermissions([
    RolePermission.LpEditAccountingPeriod,
  ]);

  const handleResetPeriod = () => {
    if (!vehicle?._id || !periodId) return;
    resetPeriodMutation.mutate({ vehicleId: vehicle._id, id: periodId });
  };

  const handleApprove = () => {
    if (!vehicle?._id || !periodId) return;
    setIsProcessingApproval(true);
    approvePeriodMutation.mutate(
      { vehicleId: vehicle._id, id: periodId, approve: true },
      {
        onSuccess: () => success('Period-end review approved successfully.'),
        onSettled: () => setIsProcessingApproval(false),
      },
    );
  };

  const handleUnlockConfirm = () => {
    if (!vehicle?._id || !periodId) return;
    approvePeriodMutation.mutate(
      { vehicleId: vehicle._id, id: periodId, approve: false },
      { onSuccess: () => setUnlockModalOpen(false) },
    );
  };

  const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;

  useEffect(() => {
    if (breadcrumbs[breadcrumbs.length - 1] === dateRange) return;
    setBreadcrumbs([
      { label: 'Period-End Review', link: LP_PERIOD_END_REVIEW },
      dateRange,
    ]);
  }, [dateRange, breadcrumbs]);

  const isApproved = period.status === AccountingPeriodStatus.Approved;
  const allRequiredConfirmed = requiredItems.every(
    (i) => period.checklist[i.key].confirmed,
  );

  return (
    <Page
      tabTitle="Period-End Review"
      heading={dateRange}
      withHeadingDivider
      spacing="lg"
      goBackAction={LP_PERIOD_END_REVIEW}
      actionBar={
        isProcessingApproval ? null : (
          <Row fullWidth justify="flex-end">
            {isApproved ? (
              <Tooltip
                title={
                  !period.isLatestApprovedPeriod
                    ? 'Only the most recent accounting period can be re-opened. Please re-open later periods first.'
                    : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={
                      approvePeriodMutation.isPending ||
                      !period.isLatestApprovedPeriod
                    }
                    onClick={() => setUnlockModalOpen(true)}
                  >
                    Unlock period
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="By approving, you will lock this period for review purposes and accounting statements will be automatically generated.">
                <span>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={
                      !allRequiredConfirmed || approvePeriodMutation.isPending
                    }
                    onClick={handleApprove}
                  >
                    Approve current period end
                  </Button>
                </span>
              </Tooltip>
            )}
          </Row>
        )
      }
    >
      {match(activeSidebar)
        .with({ type: 'unrealisedFxGains' }, () => (
          <PostUnrealisedFXGains onExit={closeSidebar} />
        ))
        .with({ type: 'companyValuations' }, () => (
          <BulkRevaluations onExit={closeSidebar} />
        ))
        .with({ type: 'prepayments' }, () => (
          <ApprovePrepayments onExit={closeSidebar} />
        ))
        .with({ type: 'accruals' }, () => (
          <ReviewAccruals onExit={closeSidebar} />
        ))
        .with({ type: 'transactionRates' }, () => (
          <ApproveTransactionRates onExit={closeSidebar} />
        ))
        .with({ type: 'bankBalances' }, () => (
          <BankBalances onExit={closeSidebar} periodEnd={period.periodEnd} />
        ))
        .otherwise(() => null)}
      {isProcessingApproval ? (
        <ProcessingPage
          title="Processing current period end"
          subtitle="This will only take a moment."
        />
      ) : (
        <PeriodEndReviewChecklist
          period={period}
          isApproved={isApproved}
          onOpenSidebar={openSidebar}
        />
      )}
      <ConfirmDialog
        open={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        title="Unlock period"
        content={
          <Typography variant="body2">
            Unlocking a period end and making changes will result in changes to
            accounting statements. If these have been sent to investors, they
            will need to be reissued.
          </Typography>
        }
        loading={approvePeriodMutation.isPending}
        actions={{
          cancel: { label: 'Cancel' },
          confirm: { onClick: handleUnlockConfirm, label: 'Unlock period' },
        }}
      />
      <ConfirmDialog
        open={refreshModalOpen}
        onClose={() => setRefreshModalOpen(false)}
        title="Period end review needs updating"
        content={
          <Typography variant="body2">
            Actions have been modified in the system for this period since it
            was last updated. This means some of the confirmed items for this
            period may be incorrect. Would you like to reset the period? This
            will unconfirm any checked items and delete any transaction sets
            created as part of the period review.
          </Typography>
        }
        loading={resetPeriodMutation.isPending}
        actions={{
          cancel: { label: 'Cancel' },
          confirm: {
            onClick: handleResetPeriod,
            label: 'Reset period',
            disabled: !canEditPeriod,
          },
        }}
      />
    </Page>
  );
};

export default PeriodEndReviewDetail;
